const CONSTRAINT_MESSAGES = {
  ux_salarycomponents_org_name:
    "A salary component with this name already exists. Use a different name or edit the existing component.",
  ux_salary_component_groups_org_code: "A component group with this code already exists.",
  ux_salary_component_group_items_group_component: "This component is already in the group.",
  ux_salary_derived_variables_org_code: "A formula variable with this code already exists.",
  ux_salary_template_groups_template_group: "This group is already linked to the template.",
  ux_org_statutory_configs_org_code_from:
    "A statutory configuration for this type and effective date already exists.",
  ux_salarytemplates_org_name: "A salary template with this name already exists.",
};

function formatStructuredDetail(detail) {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return "";
  const parts = [];
  if (detail.message) parts.push(String(detail.message));
  else if (detail.code) parts.push(String(detail.code));
  if (detail.hint && !parts.includes(String(detail.hint))) parts.push(String(detail.hint));
  if (Array.isArray(detail.steps) && detail.steps.length) {
    parts.push(detail.steps.map(String).join(" → "));
  }
  if (detail.request_id) parts.push(`(ref: ${detail.request_id})`);
  return parts.length ? parts.join(" — ") : "";
}

function extractRaw(err) {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (typeof err === "number" || typeof err === "boolean") return String(err);
  if (typeof err.detail === "string") return err.detail;
  if (err.detail && typeof err.detail === "object") {
    const structured = formatStructuredDetail(err.detail);
    if (structured) return structured;
  }
  if (typeof err.message === "string" && !err.response) return err.message;
  const data = err?.response?.data;
  if (!data) return err?.message || "";
  const detail = data?.detail ?? data?.message ?? data?.error;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const firstMsg = detail.find((x) => typeof x?.msg === "string")?.msg;
    if (firstMsg) return firstMsg;
    const asStrings = detail.filter((x) => typeof x === "string");
    if (asStrings.length) return asStrings.join(", ");
  }
  if (detail && typeof detail === "object") {
    const structured = formatStructuredDetail(detail);
    if (structured) return structured;
    return detail.msg || detail.message || "";
  }
  return err?.message || "";
}

/** Coerce API errors, structured detail objects, or plain values to display text. */
export function toDisplayString(value, fallback = "") {
  if (value == null || value === "") return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  const raw = extractRaw(value);
  if (raw) return raw;
  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
}

function matchConstraint(raw) {
  const lower = String(raw).toLowerCase();
  for (const [key, message] of Object.entries(CONSTRAINT_MESSAGES)) {
    if (lower.includes(key)) return message;
  }
  return null;
}

function stripTechnicalNoise(raw) {
  const text = String(raw || "").trim();
  if (!text) return "";

  const constraintMsg = matchConstraint(text);
  if (constraintMsg) return constraintMsg;

  const lower = text.toLowerCase();
  if (
    lower.includes("integrityerror") ||
    lower.includes("asyncpg") ||
    lower.includes("uniqueviolation") ||
    lower.includes("duplicate key") ||
    lower.includes("[sql:")
  ) {
    if (lower.includes("duplicate key") || lower.includes("unique constraint") || lower.includes("uniqueviolation")) {
      return "This record already exists. Please use a different name or code.";
    }
    if (lower.includes("foreign key")) {
      return "Cannot save because a related record is missing or still in use.";
    }
    if (lower.includes("not null")) {
      return "Required fields are missing. Please complete all mandatory fields.";
    }
    return "Could not save your changes. Please check your input and try again.";
  }

  const prefixes = [
    /^component creation failed:\s*/i,
    /^component update failed:\s*/i,
    /^group creation failed:\s*/i,
    /^group update failed:\s*/i,
    /^derived variable creation failed:\s*/i,
    /^derived variable update failed:\s*/i,
    /^statutory config creation failed:\s*/i,
  ];
  let cleaned = text;
  for (const prefix of prefixes) {
    cleaned = cleaned.replace(prefix, "");
  }
  cleaned = matchConstraint(cleaned) || cleaned;

  if (cleaned.length > 220) {
    return `${cleaned.slice(0, 217).trim()}…`;
  }
  return cleaned;
}

export function normalizeApiError(err) {
  const raw = extractRaw(err);
  const cleaned = stripTechnicalNoise(raw);
  return cleaned || "Something went wrong. Please try again.";
}

export function classifyApiError(err, fallback = "Something went wrong. Please try again.") {
  const raw = extractRaw(err);
  const message = stripTechnicalNoise(raw) || fallback;
  const lower = `${raw} ${message}`.toLowerCase();

  if (/duplicate|already exists|unique constraint|uniqueviolation/.test(lower)) {
    return {
      type: "duplicate",
      title: "Duplicate entry",
      message,
      hint: "Try a different name or code, or edit the existing record instead.",
    };
  }
  if (/required|invalid|validation|must be|missing/.test(lower)) {
    return {
      type: "validation",
      title: "Invalid input",
      message,
      hint: "Review the highlighted fields and correct any missing or invalid values.",
    };
  }
  if (/not found|404/.test(lower)) {
    return {
      type: "not_found",
      title: "Not found",
      message,
    };
  }
  if (/forbidden|permission|403|not allowed/.test(lower)) {
    return {
      type: "forbidden",
      title: "Access denied",
      message,
    };
  }
  if (/network|timeout|econnrefused|failed to fetch/.test(lower)) {
    return {
      type: "network",
      title: "Connection problem",
      message: "Could not reach the server. Check your connection and try again.",
    };
  }
  return {
    type: "error",
    title: "Action failed",
    message,
  };
}
